import { Controller } from "@hotwired/stimulus"

// This controller handles the like button functionality
// It provides immediate visual feedback and updates the count
export default class extends Controller {
    static targets = ["icon", "count"]
    static values = {
        liked: { type: Boolean, default: false },
        count: { type: Number, default: 0 },
        iconSize: { type: String, default: "size-4" }
    }

    connect() {
        this.isAnimating = false

        // Make sure the count is displayed correctly on initial load
        if (this.hasCountTarget) {
            this.countTarget.textContent = this.countValue

            if (this.countValue > 0) {
                this.countTarget.classList.remove("opacity-0")
                this.countTarget.classList.add("opacity-100")
            } else {
                this.countTarget.classList.remove("opacity-100")
                this.countTarget.classList.add("opacity-0")
            }
        }

        // Make sure the heart has the correct appearance on initial load
        this.updateHeartAppearance()
    }

    toggle(event) {
        event.preventDefault()

        if (this.isAnimating) return

        this.isAnimating = true

        // Get the form element
        const form = event.currentTarget

        // Toggle liked state for immediate visual feedback
        this.likedValue = !this.likedValue

        // Update count immediately with JS for visual feedback
        if (this.likedValue) {
            this.countValue += 1
        } else {
            this.countValue = Math.max(0, this.countValue - 1)
        }

        // Update count display
        if (this.hasCountTarget) {
            this.countTarget.textContent = this.countValue

            if (this.countValue > 0) {
                this.countTarget.classList.remove("opacity-0")
                this.countTarget.classList.add("opacity-100")
            } else {
                this.countTarget.classList.remove("opacity-100")
                this.countTarget.classList.add("opacity-0")
            }
        }

        // Animate heart
        const iconTarget = this.iconTarget
        iconTarget.classList.add("animate-heart-pop")
        this.updateHeartAppearance()

        // Submit form in the background using fetch
        const url = form.action
        const method = form.method || 'post'
        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

        // Create FormData from the form to include all fields (including _method)
        const formData = new FormData(form)

        fetch(url, {
            method: method.toUpperCase(),
            headers: {
                'X-CSRF-Token': csrfToken,
                'Accept': 'text/html',
                'X-Requested-With': 'XMLHttpRequest'
            },
            body: formData,
            credentials: 'same-origin'
        })
            .then(response => {
                if (!response.ok) {
                    throw new Error('Network response was not ok')
                }
                // Response is ignored - optimistic UI already updated
            })
            .catch(error => {
                console.error('Error submitting form:', error)
                // Revert the UI changes if there was an error
                this.likedValue = !this.likedValue
                if (this.likedValue) {
                    this.countValue += 1
                } else {
                    this.countValue = Math.max(0, this.countValue - 1)
                }
                if (this.hasCountTarget) {
                    this.countTarget.textContent = this.countValue
                }
                this.updateHeartAppearance()
            })
            .finally(() => {
                // Reset animation state
                setTimeout(() => {
                    iconTarget.classList.remove("animate-heart-pop")
                    this.isAnimating = false
                }, 300)
            })
    }

    likedValueChanged() {
        this.updateHeartAppearance()
    }

    updateHeartAppearance() {
        const heartDiv = this.iconTarget.querySelector('div')

        if (this.likedValue) {
            // Update to filled heart
            heartDiv.innerHTML = `<div class="text-red-500 fill-red-500">
                ${this.createHeartSolidIcon()}
            </div>`
        } else {
            // Update to empty heart
            heartDiv.innerHTML = `<div class="fill-none text-warm-gray-800">
                ${this.createHeartEmptyIcon()}
            </div>`
        }
    }

    createHeartSolidIcon() {
        return `<svg class="${this.iconSizeValue}" data-slot="icon" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="m11.645 20.91-.007-.003-.022-.012a15.247 15.247 0 0 1-.383-.218 25.18 25.18 0 0 1-4.244-3.17C4.688 15.36 2.25 12.174 2.25 8.25 2.25 5.322 4.714 3 7.688 3A5.5 5.5 0 0 1 12 5.052 5.5 5.5 0 0 1 16.313 3c2.973 0 5.437 2.322 5.437 5.25 0 3.925-2.438 7.111-4.739 9.256a25.175 25.175 0 0 1-4.244 3.17 15.247 15.247 0 0 1-.383.219l-.022.012-.007.004-.003.001a.752.752 0 0 1-.704 0l-.003-.001Z"></path>
        </svg>`
    }

    createHeartEmptyIcon() {
        return `<svg class="${this.iconSizeValue} stroke-[1.8px]" data-slot="icon" fill="none" stroke-width="1.5" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12Z"></path>
        </svg>`
    }
} 